import { Reducer } from 'react';
import { MockServiceDetailsActionTypes } from 'store/mockServices/mockServiceDetail/mockServiceDetailActionTypes';
import _ from 'lodash';
import { MockServiceDetailsState } from 'store/mockServices/mockServiceDetail/mockServiceDetailTypes';
import { updateTransactionById } from 'utils/transactionUtils';
import { PanePosition, TransactionShuttleState } from '../../transactionShuttle/TransactionShuttleTypes';
import { AssignmentActionType } from '../../../components/transactionsShuttle/TransactionShuttle.types';
import { PaneState, ShuttleState } from '../../../components/shuttle/Shuttle';

export const initialTxnShuttleState: TransactionShuttleState = {
  leftPane: {
    searchTerm: '',
    selected: [],
    activePage: 1,
    expanded: null,
    totalResults: 0,
    isLoading: true,
    pageSize: 8,
  },
  rightPane: {
    searchTerm: '',
    selected: [],
    activePage: 1,
    expanded: 75,
    totalResults: 0,
    isLoading: true,
    pageSize: 10,
    showPriorityModal: false,
  },
  leftPaneTransactions: [],
  rightPaneTransactions: [],
  isMoveLeftInProgress: false,
  isMoveRightInProgress: false,
};

const paneDefaults: PaneState = {
  data: [],
  selected: [],
  expanded: null,
  searchTerm: '',
  activePage: 1,
  isLoading: false,
  totalResults: 2,
  pageSize: 10,
  sort: { sortKey: 'name', direction: 'asc' },
  selectAll: null,
};

const initialRecordingShuttleState: ShuttleState = {
  isMoveLeftInProgress: false,
  isMoveRightInProgress: false,
  leftPane: paneDefaults,
  rightPane: paneDefaults,
};

const initialState: MockServiceDetailsState = {
  txnShuttle: initialTxnShuttleState,
  showMore: false,
  assets: {
    isLoading: false,
    assetBundle: {
      assets: null,
    },
  },
  messagingConfig: {
    activeTab: 'basics',
  },
  runnerTab: 'httpRunner', //selected runner
  httpRunnerTab: 'main', // tabs inside http runner
  messagingRunnerTab: 'main', // tabs inside http runner
  recordingConfigTab: 'settings', // tabs inside recording
  recordings: {
    messages: [],
    name: '',
    totalMessages: 0,
    isPolling: false,
    isSaving: false,
  },
  httpRecordings: {
    messages: [],
    totalMessages: 0,
    isPolling: false,
    isSaving: false,
  },
  recordingShuttle: initialRecordingShuttleState,
};
export const mockServiceDetailsReducer: Reducer<MockServiceDetailsState, MockServiceDetailsActionTypes> = (
  state: any = initialState,
  action,
) => {
  switch (action.type) {
    case 'UPDATE_SHUTTLE_TRANSACTIONS':
      const mockServiceDetailState = { ...state };
      let paneControls;
      if (action.payload.type === 'LEFT') {
        paneControls = mockServiceDetailState.txnShuttle.leftPane;
        mockServiceDetailState.txnShuttle.leftPaneTransactions = action.payload.transactions;
      } else {
        paneControls = mockServiceDetailState.txnShuttle.rightPane;
        mockServiceDetailState.txnShuttle.rightPaneTransactions = action.payload.transactions;
      }

      // Update total results
      paneControls.totalResults = action.payload.totalResults;
      paneControls.isLoading = false;
      return mockServiceDetailState;

    case 'PANE_IS_LOADING':
      const loadingState = _.cloneDeep(state);
      if (action.payload.type === 'LEFT') {
        loadingState.txnShuttle.leftPane.isLoading = action.payload.isLoading;
      } else {
        loadingState.txnShuttle.rightPane.isLoading = action.payload.isLoading;
      }
      return loadingState;

    case 'UPDATE_SHUTTLE_ACTIVE_PAGE':
      const paginationState = _.cloneDeep(state);
      if (action.payload.type === 'LEFT') {
        paginationState.txnShuttle.leftPane.activePage = action.payload.activePage;
      } else {
        paginationState.txnShuttle.rightPane.activePage = action.payload.activePage;
      }
      return paginationState;

    case 'UPDATE_SHUTTLE_SORTING': {
      const sortingState = _.cloneDeep(state);
      if (action.payload.type === 'LEFT') {
        sortingState.txnShuttle.leftPane.sort = action.payload.sort;
      } else {
        sortingState.txnShuttle.rightPane.sort = action.payload.sort;
      }
      return sortingState;
    }

    case 'TOGGLE_SHOW_MORE': {
      return { ...state, showMore: !state.showMore };
    }

    case 'RESET_MOCK_SERVICE_DETAIL': {
      return initialState;
    }

    case 'UPDATE_SHUTTLE_SEARCH_TERM':
      const searchTermState = _.cloneDeep(state);
      const selector = getSelectorForShuttlePane(action.payload.type);
      searchTermState.txnShuttle[selector].searchTerm = action.payload.searchTerm;

      //Go to the first page
      searchTermState.txnShuttle[selector].activePage = 1;

      return searchTermState;

    case 'RESET_SELECT_ALL': {
      const updatedState = _.cloneDeep(state);
      updatedState.txnShuttle.leftPane.selectAll = null;
      updatedState.txnShuttle.rightPane.selectAll = null;
      return updatedState;
    }

    case 'SET_ACTIVE_MESSAGING_TAB': {
      const update = _.cloneDeep(state);
      update.messagingConfig.activeTab = action.payload;
      return update;
    }

    case 'SET_ASSET_BUNDLE': {
      return { ...state, assets: { ...state.assets, assetBundle: action.payload } };
    }

    case 'SET_CERTIFICATES_IS_LOADING': {
      return { ...state, assets: { ...state.assets, isLoading: action.payload } };
    }

    case 'UPDATE_SHUTTLE_TRANSACTION': {
      const updatedState = _.cloneDeep(state);
      if (action.payload.type === 'LEFT') {
        updatedState.txnShuttle.leftPaneTransactions = updateTransactionById(
          updatedState.txnShuttle.leftPaneTransactions,
          action.payload.transaction,
        );
      } else {
        updatedState.txnShuttle.rightPaneTransactions = updateTransactionById(
          updatedState.txnShuttle.rightPaneTransactions,
          action.payload.transaction,
        );
      }
      return updatedState;
    }
    case 'UPDATE_SHUTTLE_TRANSACTION_SELECTION': {
      const stateUpdate = _.cloneDeep(state);
      const selector = getSelectorForShuttlePane(action.payload.type);
      const isSelectAll = action.payload.selectAll === 'ALL';
      stateUpdate.txnShuttle[selector].selected = isSelectAll ? [] : action.payload.transactions;
      stateUpdate.txnShuttle[selector].selectAll = action.payload.selectAll;
      return stateUpdate;
    }
    case 'UPDATE_SHUTTLE_MOVE_IN_PROGRESS':
      const inProgressState = _.cloneDeep(state);
      if (action.payload.type === AssignmentActionType.ADD) {
        inProgressState.txnShuttle.isMoveLeftInProgress = action.payload.inProgress;
      } else {
        inProgressState.txnShuttle.isMoveRightInProgress = action.payload.inProgress;
      }
      return inProgressState;

    case 'EXPAND_SHUTTLE_TXN_ITEM':
      const expandItemState = _.cloneDeep(state);
      let shuttlePane;
      if (action.payload.type === 'LEFT') {
        shuttlePane = expandItemState.txnShuttle.leftPane;
      } else {
        shuttlePane = expandItemState.txnShuttle.rightPane;
      }

      //Toggle pane
      if (shuttlePane.expanded === null || shuttlePane.expanded !== action.payload.expandItem) {
        shuttlePane.expanded = action.payload.expandItem;
      } else {
        shuttlePane.expanded = null;
      }

      return expandItemState;

    case 'RESET_TRANSACTION_SHUTTLE':
      return { ...state, txnShuttle: initialTxnShuttleState };

    case 'UPDATE_RUNNER_TAB':
      return { ...state, runnerTab: action.payload };

    case 'HTTP_RUNNER_UPDATE_TAB':
      return { ...state, httpRunnerTab: action.payload };

    case 'MESSAGING_RUNNER_UPDATE_TAB':
      return { ...state, messagingRunnerTab: action.payload };

    case 'MESSAGING_RECORDED_UPDATE_TAB':
      return { ...state, recordingConfigTab: action.payload };

    case 'MESSAGING_UPDATE_POLLING_STATUS': {
      const update = _.cloneDeep(state);
      update.recordings.isPolling = action.payload;
      return update;
    }

    case 'HTTP_UPDATE_POLLING_STATUS': {
      const update = _.cloneDeep(state);
      update.httpRecordings.isPolling = action.payload;
      return update;
    }

    case 'UPDATE_TRANSACTION_PRIORITY':
      const updatedTransactionPriorityState = _.cloneDeep(state);

      // We update only transactions in the right pane, because left pane is ready only at this point in time
      const shuttleTransactions = updatedTransactionPriorityState.txnShuttle.rightPaneTransactions;
      const transactionIndex = _.findIndex(shuttleTransactions, (item: any) => {
        return item.id === action.payload.transactionId;
      });

      updatedTransactionPriorityState.txnShuttle.rightPaneTransactions[transactionIndex].priority =
        action.payload.priority;
      return updatedTransactionPriorityState;

    case 'UPDATE_MS_TRANSACTION_FIELD': {
      const update = _.cloneDeep(state);
      const transactions = update.txnShuttle.rightPaneTransactions;
      const transactionIndex = _.findIndex(transactions, (item: any) => {
        return item.id === action.payload.transactionId;
      });
      transactions[transactionIndex][action.payload.fieldName] = action.payload.value;
      return update;
    }

    case 'SET_MOCK_SERVICE':
      return {
        ...state,
        mockService: action.payload.mockService,
      };

    case 'UPDATE_SHOW_PRIORITY_MODAL': {
      const update = _.cloneDeep(state);
      update.txnShuttle.rightPane.showPriorityModal = action.payload;
      return update;
    }

    case 'MESSAGING_UPDATE_RECORDING_NAME': {
      const update = _.cloneDeep(state);
      update.recordings.name = action.payload;
      return update;
    }

    case 'RECORDING_SAVE_IN_PROGRESS': {
      const update = _.cloneDeep(state);
      update.recordings.isSaving = action.payload;
      return update;
    }
    case 'HTTP_RECORDING_SAVE_IN_PROGRESS': {
      const update = _.cloneDeep(state);
      update.httpRecordings.isSaving = action.payload;
      return update;
    }

    case 'RECORDING_SHUTTLE_UPDATE_PANE': {
      const update = _.cloneDeep(state);
      if (action.payload.position === 'LEFT') {
        update.recordingShuttle.leftPane = action.payload.state;
      } else if (action.payload.position === 'RIGHT') {
        update.recordingShuttle.rightPane = action.payload.state;
      } else {
        throw Error("Invalid position. Must be 'LEFT' or 'RIGHT'");
      }
      return update;
    }

    case 'RECORDING_SHUTTLE_UPDATE_IN_PROGRESS': {
      const update = _.cloneDeep(state);
      if (!_.isNil(action.payload.isMoveLeftInProgress)) {
        update.recordingShuttle.isMoveLeftInProgress = action.payload.isMoveLeftInProgress;
      }

      if (!_.isNil(action.payload.isMoveRightInProgress)) {
        update.recordingShuttle.isMoveRightInProgress = action.payload.isMoveRightInProgress;
      }

      return update;
    }

    case 'MESSAGING_UPDATE_RECORDED_MESSAGES': {
      const update = _.cloneDeep(state);
      update.recordings.messages = action.payload;
      update.recordings.totalMessages = _.size(action.payload);
      return update;
    }
    case 'HTTP_UPDATE_RECORDED_MESSAGES': {
      const update = _.cloneDeep(state);
      update.httpRecordings.messages = action.payload;
      update.httpRecordings.totalMessages = _.size(action.payload);
      return update;
    }
    default:
      return state;
  }
};

const getSelectorForShuttlePane = (type: PanePosition) => {
  return type === 'LEFT' ? 'leftPane' : 'rightPane';
};
